import { useCallback, useRef, useState } from 'react';

/**
 * Calf Raise Counter using simplified heel-to-toe relative position detection.
 * Based on the proven algorithm: heel rises relative to toe (which stays grounded).
 */

// MediaPipe Pose Landmark indices - RIGHT LEG
const LANDMARKS = {
  RIGHT_HIP: 24,
  RIGHT_KNEE: 26,
  RIGHT_ANKLE: 28,
  RIGHT_HEEL: 30,
  RIGHT_FOOT_INDEX: 32
};

// Configuration - tuned thresholds from working implementation
const CONFIG = {
  VIS_MIN: 0.5,           // Minimum visibility for landmarks
  RISE_THRESH: 0.008,     // Heel rise threshold to detect "up" phase
  FALL_THRESH: 0.004,     // Heel fall threshold to detect "down" phase  
  DEBOUNCE_MS: 500,       // Minimum time between reps
  CALIB_FRAMES: 20,       // Number of frames for calibration
  NORM_RANGE: 0.07        // Normalization range for lift percentage
};

// Get landmark with visibility check
const getLandmark = (landmarks, idx) => {
  const lm = landmarks[idx];
  if (!lm) return null;
  return { x: lm.x, y: lm.y, visibility: lm.visibility || 0 };
};

export const useHeelRaiseCounter = () => {
  // State
  const [repCount, setRepCount] = useState(0);
  const [isInRaise, setIsInRaise] = useState(false);
  const [feedback, setFeedback] = useState('Position your RIGHT side to the camera');
  const [heelRiseAmount, setHeelRiseAmount] = useState(0);
  const [kneeAngle, setKneeAngle] = useState(180);
  const [footAngle, setFootAngle] = useState(0);
  
  const [detectionStatus, setDetectionStatus] = useState({
    hip: false, knee: false, ankle: false, heel: false, toe: false, 
    allDetected: false, positionLocked: false
  });
  
  const [calibrationState, setCalibrationState] = useState('waiting');
  const [calibrationProgress, setCalibrationProgress] = useState(0);
  const [readyToStart, setReadyToStart] = useState(false);
  
  // Refs
  const calibratedRef = useRef(false);
  const calibSamples = useRef([]);
  const calibFrames = useRef(0);
  
  // Baseline - heel position relative to toe
  const baselineY = useRef(null);
  
  // Rep counting state
  const repCountRef = useRef(0);
  const phaseRef = useRef('idle'); // 'idle', 'down', 'up'
  const lastRepTime = useRef(0);
  const peakLift = useRef(0);
  const liftHistory = useRef([]);

  // Check landmark detection for RIGHT leg
  const checkLandmarkDetection = useCallback((landmarks) => {
    const hip = getLandmark(landmarks, LANDMARKS.RIGHT_HIP);
    const knee = getLandmark(landmarks, LANDMARKS.RIGHT_KNEE);
    const ankle = getLandmark(landmarks, LANDMARKS.RIGHT_ANKLE);
    const heel = getLandmark(landmarks, LANDMARKS.RIGHT_HEEL);
    const toe = getLandmark(landmarks, LANDMARKS.RIGHT_FOOT_INDEX);

    const status = {
      hip: hip && hip.visibility >= CONFIG.VIS_MIN,
      knee: knee && knee.visibility >= CONFIG.VIS_MIN,
      ankle: ankle && ankle.visibility >= CONFIG.VIS_MIN,
      heel: heel && heel.visibility >= CONFIG.VIS_MIN,
      toe: toe && toe.visibility >= CONFIG.VIS_MIN,
      allDetected: false,
      positionLocked: calibratedRef.current
    };
    
    status.allDetected = status.hip && status.knee && status.ankle && status.heel && status.toe;
    
    setDetectionStatus(status);
    
    return { status, landmarks: { hip, knee, ankle, heel, toe } };
  }, []);

  // Main processing
  const processLandmarks = useCallback((landmarks) => {
    if (!landmarks || !Array.isArray(landmarks) || landmarks.length < 33) {
      setFeedback('No pose detected - step back from camera');
      setDetectionStatus({
        hip: false, knee: false, ankle: false, heel: false, toe: false, 
        allDetected: false, positionLocked: false
      });
      return null;
    }

    // Get RIGHT leg landmarks
    const { status, landmarks: lm } = checkLandmarkDetection(landmarks);

    // Check foot visibility
    if (!status.heel || !status.toe || !status.ankle) {
      const missing = [];
      if (!status.heel) missing.push('heel');
      if (!status.toe) missing.push('toe');
      if (!status.ankle) missing.push('ankle');
      setFeedback(`Foot not visible: ${missing.join(', ')} - include full foot in frame`);
      return null;
    }

    const { heel, toe, ankle } = lm;

    // Calculate heel rise relative to toe (toe stays grounded, heel.y drops as heel rises)
    const heelRelY = toe.y - heel.y;

    // Calibration phase
    if (!calibratedRef.current) {
      calibSamples.current.push(heelRelY);
      calibFrames.current++;
      
      const pct = Math.round((calibFrames.current / CONFIG.CALIB_FRAMES) * 100);
      setCalibrationProgress(pct);
      setCalibrationState('calibrating');
      setFeedback(`Calibrating... ${pct}% - keep foot FLAT on floor`);
      
      if (calibFrames.current >= CONFIG.CALIB_FRAMES) {
        // Calculate baseline as average of samples
        baselineY.current = calibSamples.current.reduce((a, b) => a + b, 0) / calibSamples.current.length;
        calibSamples.current = [];
        calibratedRef.current = true;
        
        setCalibrationState('complete');
        setReadyToStart(true);
        phaseRef.current = 'down';
        setFeedback('Ready - start your calf raises!');
        console.log('✓ Calibration complete, baseline:', baselineY.current);
      }
      return null;
    }

    // Calculate lift amount
    const lift = heelRelY - baselineY.current;
    const pct = Math.min(100, Math.max(0, (lift / CONFIG.NORM_RANGE) * 100));
    
    // Update UI metrics
    setHeelRiseAmount(pct / 100);
    setFootAngle(Math.round(pct));
    
    // Track peak in current raise
    if (pct > peakLift.current) {
      peakLift.current = pct;
    }

    const now = Date.now();
    const currentPhase = phaseRef.current;

    // Phase state machine
    if (currentPhase === 'down' && lift > CONFIG.RISE_THRESH) {
      // Transitioning UP
      phaseRef.current = 'up';
      setIsInRaise(true);
      setFeedback('Rising... keep going higher!');
      
    } else if (currentPhase === 'up' && lift < CONFIG.FALL_THRESH) {
      // Transitioning DOWN - count rep
      const timeSinceLastRep = now - lastRepTime.current;
      
      if (timeSinceLastRep > CONFIG.DEBOUNCE_MS) {
        repCountRef.current++;
        lastRepTime.current = now;
        
        // Store lift quality
        liftHistory.current.push(Math.round(peakLift.current));
        peakLift.current = 0;
        
        setRepCount(repCountRef.current);
        setKneeAngle(Math.round(pct)); // Use as quality indicator
        
        console.log('✓ Rep:', repCountRef.current);
        setFeedback(`Rep ${repCountRef.current} ✓ - lower and repeat`);
      }
      
      phaseRef.current = 'down';
      setIsInRaise(false);
      
    } else if (currentPhase === 'up') {
      setFeedback('Good - lower heel to complete rep');
    } else {
      // Still down, waiting for raise
      if (pct < 10) {
        setFeedback('Raise your heel - rise up on your toes');
      } else {
        setFeedback('Keep rising higher...');
      }
    }

    return {
      lift,
      liftPct: pct,
      phase: phaseRef.current,
      isInRaise: phaseRef.current === 'up'
    };
  }, [checkLandmarkDetection]);

  // Reset
  const reset = useCallback(() => {
    console.log('=== RESET ===');
    setRepCount(0);
    setIsInRaise(false);
    setFeedback('Position your RIGHT side to the camera');
    setHeelRiseAmount(0);
    setKneeAngle(180);
    setFootAngle(0);
    setDetectionStatus({
      hip: false, knee: false, ankle: false, heel: false, toe: false, 
      allDetected: false, positionLocked: false
    });
    setCalibrationState('waiting');
    setCalibrationProgress(0);
    setReadyToStart(false);
    
    calibratedRef.current = false;
    calibSamples.current = [];
    calibFrames.current = 0;
    baselineY.current = null;
    
    repCountRef.current = 0;
    phaseRef.current = 'idle';
    lastRepTime.current = 0;
    peakLift.current = 0;
    liftHistory.current = [];
  }, []);

  const isCalibrated = useCallback(() => calibratedRef.current, []);

  return {
    repCount,
    isInRaise,
    feedback,
    heelRiseAmount,
    kneeAngle,
    footAngle,
    detectionStatus,
    calibrationState,
    calibrationProgress,
    readyToStart,
    processLandmarks,
    reset,
    isCalibrated,
    setRepCount
  };
};

export default useHeelRaiseCounter;
